'use client';

import { createContext, useState, useEffect, ReactNode } from 'react';
import { usePathname } from 'next/navigation';

type ActivePage = 'home' | 'about' | 'work' | 'contact' | 'imprint' | 'privacy' | 'cv' | null;

interface AppContextType {
  activePage: ActivePage;
  setActivePage: (page: ActivePage) => void;
  isOnCvSubdomain: boolean;
}

export const AppContext = createContext<AppContextType>({
                                                          activePage: null,
                                                          setActivePage: () => {},
                                                          isOnCvSubdomain: false
                                                        });

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const [activePage, setActivePage] = useState<ActivePage>(null);
  const [isOnCvSubdomain, setIsOnCvSubdomain] = useState<boolean>(false);
  const pathname = usePathname();

  // Diese Funktion erkennt die aktive Seite basierend auf dem URL-Pfad
  const setActivePageFromPath = (path: string) => {
    if (path === '/') return setActivePage('home');

    if (path.startsWith('/cv')) {
      console.log('CV Path erkannt:', path);
      return setActivePage('cv');
    }
    
    // Prüfe, ob der Pfad eine work-Unterseite ist (z.B. /work oder /work/[slug])
    if (path.startsWith('/work')) return setActivePage('work');

    if (path.startsWith('/about')) return setActivePage('about');
    if (path.startsWith('/contact')) return setActivePage('contact');
    if (path.startsWith('/imprint')) return setActivePage('imprint');
    if (path.startsWith('/privacy')) return setActivePage('privacy');

    // Für alle anderen Pfade, behalte die aktuelle Seite bei
    return setActivePage(null); // oder null, falls ein Default gewünscht ist
  };

  // Automatisches Setzen der aktiven Seite beim initialen Laden und URL-Änderungen
  useEffect(() => {
    if (pathname) {
      console.log('Pathname geändert:', pathname);
      setActivePageFromPath(pathname);
    }
  }, [pathname]);

  // Erkennung der CV-Subdomain
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const isCvSubdomain = hostname === 'cv.localhost' || 
                           hostname === 'cv.localhost:3000' || 
                           hostname.startsWith('cv.');
      console.log('Hostname erkannt:', hostname, 'isCvSubdomain:', isCvSubdomain);
      setIsOnCvSubdomain(isCvSubdomain);
    }
  }, []);

  // Effect für das Setzen des data-page-Attributs am body-Element
  useEffect(() => {
    if (activePage && typeof document !== 'undefined') {
      document.body.dataset.page = activePage;

      // Optional: Debug-Ausgabe
      console.log('Aktive Seite gesetzt:', activePage);
    }
  }, [activePage]);

  return (
    <AppContext.Provider value={{ activePage, setActivePage, isOnCvSubdomain }}>
      {children}
    </AppContext.Provider>
  );
};